import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Calendar, DollarSign, MapPin, Users, Phone, Mail, FileText,
  MessageSquare, Clock, Plus, Trash2, CheckCircle2, ChevronRight, Activity, MessageCircle
} from 'lucide-react';
import { useOportunidadeDetalhe, useComercialMutations, useEtapasConfig } from '../../api/hooks/useComercial';
import '../../components/ui/datatable.css';

const INTERACAO_ICONS: Record<string, { icon: any; color: string; bg: string }> = {
  ligacao: { icon: Phone, color: '#3b82f6', bg: '#eff6ff' },
  email: { icon: Mail, color: '#8b5cf6', bg: '#f5f3ff' },
  reuniao: { icon: Users, color: '#f59e0b', bg: '#fffbeb' },
  visita: { icon: MapPin, color: '#10b981', bg: '#ecfdf5' },
  apresentacao: { icon: Activity, color: '#ec4899', bg: '#fdf2f8' },
  proposta: { icon: FileText, color: '#ef4444', bg: '#fef2f2' }
};

export function OportunidadeDetalhePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const { data: op, isLoading, refetch } = useOportunidadeDetalhe(id);
  const { criarInteracao, atualizarEtapaOportunidade } = useComercialMutations();
  const { data: etapasRaw = [] } = useEtapasConfig();

  const ETAPAS = useMemo(() =>
    etapasRaw.sort((a, b) => a.ordem - b.ordem),
    [etapasRaw]
  );
  const ETAPAS_VISIVEIS = useMemo(() => ETAPAS.filter(e => e.visivel), [ETAPAS]);

  const [tipoInteracao, setTipoInteracao] = useState('ligacao');
  const [descInteracao, setDescInteracao] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddInteracao = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !descInteracao.trim()) return;
    
    setIsSubmitting(true);
    try {
      await criarInteracao.mutateAsync({
        oportunidadeId: id,
        tipo: tipoInteracao,
        descricao: descInteracao,
        data: new Date().toISOString(),
      });
      setDescInteracao('');
      refetch();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStageChange = async (etapa: string) => {
    if (!id) return;
    try {
      await atualizarEtapaOportunidade.mutateAsync({ id, etapaFunil: etapa });
      refetch();
    } catch (err) {
      console.error(err);
    }
  };

  if (isLoading) {
    return <div className="dt-loading">Carregando detalhes...</div>;
  }
  if (!op) {
    return <div className="dt-empty">Oportunidade não encontrada.</div>;
  }

  const currentStage = ETAPAS.find(e => e.id === op.etapaFunil) || ETAPAS[0] || { id: '', label: '-', color: '#94a3b8' };

  return (
    <div className="detail-page">
      {/* Breadcrumb Back link */}
      <button onClick={() => navigate('/comercial/oportunidades')} className="detail-back-btn">
        <ArrowLeft size={16} /> Voltar para Oportunidades
      </button>

      {/* Title Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0, color: 'var(--text-main)' }}>
            {op.titulo}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <span className="badge" style={{ background: '#eff6ff', color: '#3b82f6', textTransform: 'capitalize' }}>
              {op.tipo}
            </span>
            <span className="detail-info-label">
              Criada em {new Date(op.criadoEm).toLocaleDateString('pt-BR')}
            </span>
          </div>
        </div>

        {/* Stage selection selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="detail-info-label" style={{ fontWeight: 600 }}>Estágio:</span>
          <select
            value={op.etapaFunil}
            onChange={e => handleStageChange(e.target.value)}
            className="form-select"
            style={{ width: 'auto', borderColor: currentStage.color }}
          >
            {ETAPAS_VISIVEIS.map(et => (
              <option key={et.id} value={et.id}>{et.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 24, alignItems: 'flex-start' }}>
        {/* Main section: Interações log + Timeline */}
        <div>
          {/* Nova Interação form */}
          <div className="detail-card">
            <h2 className="detail-section-title">
              <MessageCircle size={18} style={{ color: '#6366f1' }} />
              Registrar Nova Interação
            </h2>

            <form onSubmit={handleAddInteracao} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <span className="detail-info-label" style={{ fontWeight: 600 }}>Tipo de Interação:</span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {Object.keys(INTERACAO_ICONS).map(tipo => {
                    const selected = tipoInteracao === tipo;
                    const meta = INTERACAO_ICONS[tipo];
                    const Icon = meta.icon;
                    return (
                      <button
                        key={tipo}
                        type="button"
                        onClick={() => setTipoInteracao(tipo)}
                        className={`detail-interacao-chip ${selected ? 'active' : ''}`}
                        style={selected ? { borderColor: meta.color, background: meta.bg, color: meta.color } : undefined}
                      >
                        <Icon size={12} />
                        <span style={{ textTransform: 'capitalize' }}>{tipo}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <textarea
                  value={descInteracao}
                  onChange={e => setDescInteracao(e.target.value)}
                  placeholder="Descreva o que foi conversado ou acordado nesta interação..."
                  required
                  rows={3}
                  className="form-input"
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="submit"
                  disabled={isSubmitting || !descInteracao.trim()}
                  className="btn-save"
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <Plus size={16} /> Salvar Interação
                </button>
              </div>
            </form>
          </div>

          {/* Timeline of interactions */}
          <div className="detail-card">
            <h2 className="detail-section-title" style={{ marginBottom: 20 }}>
              Linha do Tempo
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', paddingLeft: 20 }}>
              {/* Vertical line helper */}
              <div className="detail-timeline-line" />

              {(!op.interacoes || op.interacoes.length === 0) ? (
                <div className="dt-empty">
                  Nenhuma interação registrada ainda. Use o formulário acima para registrar.
                </div>
              ) : op.interacoes.map((item, idx) => {
                const meta = INTERACAO_ICONS[item.tipo] || { icon: MessageSquare, color: '#64748b', bg: '#f1f5f9' };
                const Icon = meta.icon;

                return (
                  <div key={item.id} style={{ display: 'flex', gap: 16, marginBottom: idx === op.interacoes!.length - 1 ? 0 : 24, position: 'relative' }}>
                    {/* Circle Icon anchor */}
                    <div style={{
                      position: 'absolute', left: -20, top: 4, width: 22, height: 22,
                      borderRadius: '50%', background: meta.bg, border: `2px solid ${meta.color}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1,
                      color: meta.color
                    }}>
                      <Icon size={11} />
                    </div>

                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: meta.color, textTransform: 'capitalize' }}>
                          {item.tipo}
                        </span>
                        <span className="detail-info-label">
                          {new Date(item.data).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                        </span>
                      </div>
                      <p className="detail-timeline-bubble">
                        {item.descricao}
                      </p>
                      <div className="detail-info-label" style={{ marginTop: 4, paddingLeft: 4 }}>
                        Registrado por: <strong>{item.responsavel?.nome || 'Suporte'}</strong>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Sidebar: Opportunity Meta information */}
        <div>
          <div className="detail-card">
            <h2 className="detail-section-title" style={{ fontSize: '0.95rem' }}>
              Informações do Lead
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {op.valorEstimado && (
                <div className="detail-info-row">
                  <DollarSign size={16} style={{ color: '#10b981' }} />
                  <div>
                    <div className="detail-info-label">Valor Estimado</div>
                    <strong style={{ fontSize: '1rem', color: 'var(--text-main)' }}>
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(op.valorEstimado))}
                    </strong>
                  </div>
                </div>
              )}

              {(op.municipio || op.estado) && (
                <div className="detail-info-row">
                  <MapPin size={16} style={{ color: '#ef4444' }} />
                  <div>
                    <div className="detail-info-label">Localização</div>
                    <div>{op.municipio?.nome || ''} {op.estado ? `/ ${op.estado.sigla}` : ''}</div>
                  </div>
                </div>
              )}

              {op.contato && (
                <div className="detail-info-row">
                  <Users size={16} style={{ color: '#3b82f6' }} />
                  <div>
                    <div className="detail-info-label">Contato Principal</div>
                    <div>{op.contato}</div>
                  </div>
                </div>
              )}

              {op.telefone && (
                <div className="detail-info-row">
                  <Phone size={16} style={{ color: '#f59e0b' }} />
                  <div>
                    <div className="detail-info-label">Telefone</div>
                    <a href={`tel:${op.telefone}`} style={{ color: 'var(--text-main)', textDecoration: 'none' }}>
                      {op.telefone}
                    </a>
                  </div>
                </div>
              )}

              {op.email && (
                <div className="detail-info-row">
                  <Mail size={16} style={{ color: '#ec4899' }} />
                  <div>
                    <div className="detail-info-label">E-mail</div>
                    <a href={`mailto:${op.email}`} style={{ color: 'var(--text-main)', textDecoration: 'none' }}>
                      {op.email}
                    </a>
                  </div>
                </div>
              )}

              {op.dataPrevisao && (
                <div className="detail-info-row">
                  <Calendar size={16} style={{ color: '#8b5cf6' }} />
                  <div>
                    <div className="detail-info-label">Previsão de Fechamento</div>
                    <div>{new Date(op.dataPrevisao).toLocaleDateString('pt-BR')}</div>
                  </div>
                </div>
              )}

              <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: 16, marginTop: 4 }}>
                <div className="detail-info-label">Responsável AC</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                  {op.responsavel?.fotoUrl ? (
                    <img src={op.responsavel.fotoUrl} alt={op.responsavel.nome} style={{ width: 24, height: 24, borderRadius: '50%' }} />
                  ) : (
                    <div className="detail-avatar">
                      {op.responsavel?.nome.slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)' }}>
                    {op.responsavel?.nome || '-'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {op.observacoes && (
            <div className="detail-card">
              <h2 className="detail-section-title" style={{ fontSize: '0.95rem', marginBottom: 10 }}>
                Descrição / Anotações
              </h2>
              <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-muted)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {op.observacoes}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
